<template>
  <span>
    <div
      v-if="spinner === 'double-bounce'"
      class="spin-double-bounce"
      :style="{
        width: spinnerSize,
        height: spinnerSize
      }"
    >
      <div
        class="spin-double-bounce-bounce1"
        :style="{ backgroundColor: spinnerColor }"
      />
      <div
        class="spin-double-bounce-bounce2"
        :style="{ backgroundColor: spinnerColor }"
      />
    </div>
    <div v-else-if="spinner === 'triple-bounce'" class="spin-triple-bounce">
      <div class="spin-triple-bounce-bounce1" :style="bounceStyle" />
      <div class="spin-triple-bounce-bounce2" :style="bounceStyle" />
      <div class="spin-triple-bounce-bounce3" :style="bounceStyle" />
    </div>
    <div
      v-else-if="spinner === 'fading-circle'"
      class="spin-fading-circle circle-color"
      :style="{
        width: spinnerSize,
        height: spinnerSize
      }"
    >
      <div
        :key="n"
        v-for="n in 12"
        :class="['is-circle' + (n + 1)]"
        class="spin-fading-circle-circle"
      />
    </div>
    <div
      v-else
      class="spin-snake"
      :style="{
        'border-top-color': spinnerColor,
        'border-left-color': spinnerColor,
        'border-bottom-color': spinnerColor,
        height: spinnerSize,
        width: spinnerSize
      }"
    />
  </span>
</template>

<script>
const SPINNERS = ['snake', 'double-bounce', 'triple-bounce', 'fading-circle']
const parseSpinner = (index) => {
  if ({}.toString.call(index) === '[object Number]') {
    if (SPINNERS.length <= index) {
      // console.warn(`'${index}' spinner not found, use the default spinner.`)
      index = 0
    }
    return SPINNERS[index]
  }
  if (SPINNERS.indexOf(index) === -1) {
    // console.warn(`'${index}' spinner not found, use the default spinner.`)
    index = SPINNERS[0]
  }
  return index
}
/**
 * Spin
 * @module components/spinner
 * @desc 加载动画
 * @param {string|number} [type=snake] - 显示类型，传入类型名或者类型 id，
 *        可选 `snake`, `dobule-bounce`, `triple-bounce`, `fading-circle`
 * @param {number} size - 尺寸
 * @param {string} color - 颜色
 */
export default {
  name: 'spin',
  props: {
    type: {
      default: 0
    },
    size: {
      type: Number,
      default: 28
    },
    color: {
      type: String,
      default: '#ccc'
    }
  },
  computed: {
    spinner() {
      return parseSpinner(this.type)
    },
    spinnerColor() {
      return this.color || this.$parent.color || '#ccc'
    },
    spinnerSize() {
      return (this.size || this.$parent.size || 28) + 'px'
    },
    bounceStyle() {
      const size = (this.size || this.$parent.size || 28) / 3 + 'px'
      return {
        width: size,
        height: size,
        backgroundColor: this.spinnerColor
      }
    }
  },
  created() {
    if (this.spinner !== 'fading-circle' || this.$isServer) return
    this.styleNode = document.createElement('style')
    const css = `.circle-color > div::before { background-color: ${this.spinnerColor}; }`
    this.styleNode.type = 'text/css'
    this.styleNode.rel = 'stylesheet'
    this.styleNode.title = 'fading circle style'
    document.getElementsByTagName('head')[0].appendChild(this.styleNode)
    this.styleNode.appendChild(document.createTextNode(css))
  },
  destroyed() {
    if (this.styleNode) {
      this.styleNode.parentNode.removeChild(this.styleNode)
    }
  }
}
</script>

<style lang="scss" scope>
.spin-snake {
  animation: spinner-rotate 0.8s infinite linear;
  border: 4px solid transparent;
  border-radius: 50%;
  @keyframes spinner-rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
.spin-double-bounce {
  position: relative;
  &-bounce1,
  &-bounce2 {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    opacity: 0.6;
    position: absolute;
    top: 0;
    left: 0;
    animation: spin-double-bounce 2s infinite ease-in-out;
  }
  &-bounce2 {
    animation-delay: -1s;
  }
  @keyframes spin-double-bounce {
    0%,
    100% {
      transform: scale(0);
    }
    50% {
      transform: scale(1);
    }
  }
}
.spin-triple-bounce {
  &-bounce1,
  &-bounce2,
  &-bounce3 {
    border-radius: 100%;
    display: inline-block;
    animation: spin-triple-bounce 1.4s infinite ease-in-out both;
  }
  &-bounce1 {
    animation-delay: -0.32s;
  }
  &-bounce2 {
    animation-delay: -0.16s;
  }
  @keyframes spin-triple-bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
}
.spin-fading-circle {
  position: relative;
  &-circle {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    &::before {
      content: ' ';
      display: block;
      width: 15%;
      height: 15%;
      margin: 0 auto;
      border-radius: 100%;
      animation: spin-fading-circle 1.2s infinite ease-in-out both;
    }
    @for $i from 2 to 12 {
      &.is-circle#{$i} {
        transform: rotate(360deg / 12 * ($i - 1));
        &::before {
          animation-delay: -1.2s + 1.2s / 12 * ($i - 1);
        }
      }
    }
  }
  @keyframes spin-fading-circle {
    0%,
    39%,
    100% {
      opacity: 0;
    }
    40% {
      opacity: 1;
    }
  }
}
</style>
